import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { m } from 'motion/react';
import { useId } from 'react';

/** One question and the answer shown under it. Also what `faqSchema` takes. */
export type FaqItem = { question: string; answer: string };

type Props = { item: FaqItem; open: boolean; onToggle: () => void };

/**
 * One question and its answer.
 *
 * Shared rather than page-local, which it was until /services needed the same
 * row many times over — once on the hub and once on each service
 * pages. Nothing about it was ever specific to the import-request page; it
 * takes a question, an answer and whether it is open, and the accessibility
 * work in it is the part nobody should write twice.
 *
 * Hand-built rather than a MUI Accordion so the open row is driven by the
 * band's own single-open state rather than one per row.
 *
 * Every answer stays in the DOM whether it is open or not. Unmounting the
 * closed ones would leave six of the seven answers existing only inside this
 * page's FAQPage JSON-LD — a search engine is told about answers the page does
 * not contain, and a visitor without JavaScript gets a list of questions with
 * no answers under them. `Collapse` also renders its closed state during the
 * prerender, so the page does not load open and then snap shut.
 *
 * `inert` takes a closed answer out of the tab order and off the accessibility
 * tree, which a bare `Collapse` — height zero, content still focusable — would
 * not do.
 *
 * The chevron is the one part that animates through the runtime: a spring
 * unwinds correctly when the row is toggled again mid-rotation.
 */
export function FaqRow({ item, open, onToggle }: Props) {
  const id = useId();
  const answerId = `${id}-answer`;
  const questionId = `${id}-question`;

  return (
    <Box
      component="li"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 1, borderColor: 'divider' },
      }}
    >
      <ButtonBase
        id={questionId}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={answerId}
        sx={{
          width: '100%',
          textAlign: 'left',
          justifyContent: 'space-between',
          gap: 4,
          px: { xs: 0, md: 2 },
          py: 5,
          borderRadius: 1,
          color: open ? 'primary.main' : 'text.primary',
          transition: 'color 200ms ease',
          '&:hover': { color: 'primary.main' },
        }}
      >
        <Typography variant="h6" component="h3" sx={{ flex: 1 }}>
          {item.question}
        </Typography>
        <Box
          component={m.span}
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ type: 'spring', stiffness: 500, damping: 34 }}
          sx={{ display: 'grid', placeItems: 'center', flexShrink: 0, color: 'primary.main' }}
        >
          <ExpandMoreRounded aria-hidden />
        </Box>
      </ButtonBase>

      <Collapse in={open}>
        <Box id={answerId} role="region" aria-labelledby={questionId} inert={!open}>
          <Typography color="text.secondary" sx={{ px: { xs: 0, md: 2 }, pb: 6, maxWidth: '70ch' }}>
            {item.answer}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}
